import React, { useEffect, useMemo, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader, NavMenuSlotType } from '../../../../framework/components/bwj-page-header';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { AppRoutes, getAppRouteByPathname, toProjectSlug } from '../../../app-routes';
import { ProjectOverviewService } from '../../services/project-overview';
import { IProjectOverviewModel, ProjectOverviewModel } from '../../../../entities-generated/models/project/project-overview-model';
import { toTitleText } from '../../../../framework/utils/string';
import { ProjectApiClient } from '../../../../services/project';
import { ProjectOverviewSideNav } from '../../components/overview-side-nav';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { MessageBarType } from '@fluentui/react';

const getHeading = (project: IProjectOverviewModel, projectId: string, folder: string | undefined) => {
  const title = !!folder ? toTitleText(folder) : project.customer;
  const subtitle = project.address;

  return { title, subtitle };
}

export const PageProjectOverview: React.FunctionComponent = () => {
  const { showToast } = useToast();
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [showSideMenu, setShowSideMenu] = useState(true);
  const location = useLocation();
  const { id, site } = useMemo(() => {
    const route = getAppRouteByPathname(location.pathname);
    if(!route || !route.regex) { return { id: '', site: '' }; }
    const match = location.pathname.match(route.regex)!;
    return { id: `${match[1].substring(0, 4)}.${match[1].substring(4)}`, site: match[2] };
  }, [location]);
  const [project, setProject] = useState<IProjectOverviewModel>(new ProjectOverviewModel());
  const heading = useMemo(() => getHeading(project, id, site), [project, id, site]);
  const activeKey = useMemo(() => {
    const route = getAppRouteByPathname(location.pathname);
    return route?.key ?? '';
  }, [location]);

  useEffect(() => {
    const prj = ProjectOverviewService.getOverview(id);
    if(!!prj) {
      setProject(prj);
      setViewState(BwjViewState.Ready);
    } else {
      ProjectApiClient.getProjectOverview(id)
      .then(resp => {
        ProjectOverviewService.setOverview(resp);
        setProject(resp);
        setViewState(BwjViewState.Ready);
      })
      .catch(e => {
        console.log(e);
        setViewState(BwjViewState.Error);
      });
    }
  }, [id, site]);

  const onRefresh = () => {
    setViewState(BwjViewState.Loading);

    ProjectApiClient.getProjectOverview(id)
    .then(resp => {
      ProjectOverviewService.setOverview(resp);
      setProject(resp);
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'An error occurred');
    })
    .finally(() => setViewState(BwjViewState.Ready));
  }

  const onGetFormST124 = () => {
    ProjectApiClient.getNewYorkSt124(id)
    .then(resp => {
      const wdw = window.open('', '_blank');
      if(!!wdw) {
        wdw.document.write(resp.html);
        wdw.document.close();
      }
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'An error occurred');
    })
  }

  const onViewCatalog = () => {
    window.open(`https://shop.bwjstorm.com/start/${project.catalogCode}`, '_blank');
  }

  return (
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Loading project info...'
      }}
      errorState={{
        message: 'An unexpected error occurred',
      }}
    >
      <BwjPageHeader
        title={heading.title}
        subtitle={heading.subtitle}
        titleIconName={AppRoutes.projectOverview.icon}
        navMenuSlots={[{
          name: 'main',
          slotType: NavMenuSlotType.Collapsable,
          onToggle: (menuName: string, isCollasped: boolean) => setShowSideMenu(isCollasped === false),
        }]}
        actionButtons={[{
          id: 'btnRefresh',
          name: 'Refresh',
          iconName: 'Refresh',
          disabled: false,
          onClick: onRefresh,
        }, {
          id: 'btnSt124',
          name: 'Print form NY S-124',
          iconName: 'FabricFormLibrary',
          disabled: false,
          onClick: onGetFormST124,
        }, {
          id: 'btnViewCatalog',
          name: 'Peek catalog',
          iconName: 'Shop',
          disabled: false,
          onClick: onViewCatalog,
        }]}
      />
      <BwjPageContent contentDescription={'side menus and subcontent'}>
        {showSideMenu &&
        <ProjectOverviewSideNav
          activeKey={activeKey}
          projectId={id}
          site={site}
          project={project}
        />}
        <Switch>
          <Route path={AppRoutes.projectOverview.urlTemplate} exact>
            <Redirect to={AppRoutes.projectOverviewAbout.url(toProjectSlug(id))} />
          </Route>
          <Route
            path={AppRoutes.projectOverviewAbout.urlTemplate}
            exact={AppRoutes.projectOverviewAbout.exact}
            component={AppRoutes.projectOverviewAbout.component}
          />
          <Route
            path={AppRoutes.projectOverviewMaterials.urlTemplate}
            exact={AppRoutes.projectOverviewMaterials.exact}
            component={AppRoutes.projectOverviewMaterials.component}
          />
          <Route
            path={AppRoutes.projectOverviewInstallSite.urlTemplate}
            exact={AppRoutes.projectOverviewInstallSite.exact}
            component={AppRoutes.projectOverviewInstallSite.component}
          />
        </Switch>
      </BwjPageContent>
    </BwjViewManager>
  );
};
